import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
// Number formats for the Strategy Tester, TradingView style.

const money = new Map<string, Intl.NumberFormat>()

/** Lakh grouping (1,23,456) for rupees, thousands (123,456) for everything else. */
const localeFor = (currency: string) => (currency === "INR" ? "en-IN" : "en-US")

export const formatMoney = (value: number | null | undefined, currency: string): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—"
  let format = money.get(currency)
  if (format === undefined) {
    try {
      format = new Intl.NumberFormat(localeFor(currency), { style: "currency", currency, maximumFractionDigits: 2 })
    } catch {
      format = new Intl.NumberFormat(localeFor(currency), { maximumFractionDigits: 2 })
    }
    money.set(currency, format)
  }
  return format.format(value)
}

const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 })

export const formatNumber = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? "—" : number.format(value)

const price = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 5 })

/** A fill price: at least 2 decimals, up to 5 (FX quotes). */
export const formatPrice = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? "—" : price.format(value)

/** The report's percentages are fractions: 0.15 → "15%". */
export const formatPercent = (fraction: number | null | undefined): string =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction) ? "—" : `${number.format(fraction * 100)}%`

/** The CSS class of a profit: "up" (green), "down" (red), or none for 0. */
export const tone = (value: number | null | undefined): string =>
  value === null || value === undefined || value === 0 ? "" : value > 0 ? "up" : "down"

/** A report time in Indian time (DISPLAY_TIME_ZONE): the date, plus the clock for intraday bars. */
export const formatTime = (time: number, intraday: boolean): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(intraday ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {})
  }).format(time * 1000)
